/**
 * Account-local calendar dates as 'YYYY-MM-DD' strings. Never do Date math on these in the
 * server's timezone: take "today" in the account's IANA zone, then add whole days in UTC.
 */

export function todayIn(timeZone: string, now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

export interface DateRange {
  from: string;
  to: string;
  /** previous period of the same length, ending the day before `from` */
  prevFrom: string;
  prevTo: string;
  days: number;
}

/** Last `days` complete days (ending yesterday) in the account timezone, plus the previous period. */
export function lastDays(days: number, timeZone: string, now = new Date()): DateRange {
  const to = addDays(todayIn(timeZone, now), -1);
  return rangeOf(addDays(to, -(days - 1)), to);
}

export function rangeOf(from: string, to: string): DateRange {
  const days = daysBetween(from, to) + 1;
  const prevTo = addDays(from, -1);
  return { from, to, prevTo, prevFrom: addDays(prevTo, -(days - 1)), days };
}
